/**
 * Geometry + identity metadata of a single rendered appointment preview.
 * Mirrors the layout produced by `calculateOverlapLayout`, enriched with the
 * event's time range so hover arbitration can apply duration-based priority.
 */
export interface CalendarAppointmentLayout {
  id: string;
  startMinutes: number;
  endMinutes: number;
  durationMinutes: number;
  topPx: number;
  heightPx: number;
  /** Absolute left offset from the grid edge (px). */
  leftPx: number;
  /** Absolute right offset from the grid edge (px). */
  rightPx: number;
  /** Base (non-elevated) z-index from the overlap layout. */
  zIndex: number;
  /** Stack column index within the overlap group (0-based). */
  stackIndex: number;
}

export interface ResolveAppointmentHoverParams {
  /** Pointer X relative to the grid's left edge (px). */
  pointerXPx: number;
  /** Pointer Y relative to the grid's top edge (px). */
  pointerYPx: number;
  /** Rendered width of the grid (px), used to derive each card's right edge. */
  gridWidthPx: number;
  layouts: CalendarAppointmentLayout[];
}

/**
 * A preview is a hover candidate only when the pointer is inside its rendered
 * box, both vertically (time range) and horizontally (visible strip). All cards
 * in an overlap group share the same right edge, so the horizontal test is what
 * keeps the long appointment's exposed left strip mapped to the long
 * appointment, while the shifted shorter appointment owns the area to its right.
 */
const isPointerOverAppointment = (
  layout: CalendarAppointmentLayout,
  pointerXPx: number,
  pointerYPx: number,
  gridWidthPx: number
): boolean => {
  const isWithinVertical =
    pointerYPx >= layout.topPx && pointerYPx <= layout.topPx + layout.heightPx;

  if (!isWithinVertical) {
    return false;
  }

  const rightEdgePx = gridWidthPx - layout.rightPx;

  return pointerXPx >= layout.leftPx && pointerXPx <= rightEdgePx;
};

/**
 * Deterministic priority between overlapping candidates under the pointer:
 *   1. smaller duration wins (keeps short appointments reachable inside long ones);
 *   2. then the visually topmost card (higher base z-index per overlap rules);
 *   3. then the later start time;
 *   4. then stable id ordering as the final tie-breaker.
 */
const compareHoverCandidates = (
  a: CalendarAppointmentLayout,
  b: CalendarAppointmentLayout
): number => {
  if (a.durationMinutes !== b.durationMinutes) {
    return a.durationMinutes - b.durationMinutes;
  }

  if (a.zIndex !== b.zIndex) {
    return b.zIndex - a.zIndex;
  }

  if (a.startMinutes !== b.startMinutes) {
    return b.startMinutes - a.startMinutes;
  }

  return a.id.localeCompare(b.id);
};

/**
 * Resolves which appointment preview should receive hover elevation for the
 * current pointer position. Returns the appointment id, or null when the
 * pointer is not over any preview.
 */
export const resolveActiveAppointmentId = ({
  pointerXPx,
  pointerYPx,
  gridWidthPx,
  layouts,
}: ResolveAppointmentHoverParams): string | null => {
  let bestCandidate: CalendarAppointmentLayout | null = null;

  for (const layout of layouts) {
    if (
      !isPointerOverAppointment(layout, pointerXPx, pointerYPx, gridWidthPx)
    ) {
      continue;
    }

    if (
      bestCandidate === null ||
      compareHoverCandidates(layout, bestCandidate) < 0
    ) {
      bestCandidate = layout;
    }
  }

  return bestCandidate?.id ?? null;
};
